/**
 * ChatComposer — the message field with its n / 2000 counter, Send, and an always-visible Contact
 * support control (BR-26). Send is disabled while the field is empty, over the limit, or a turn is in
 * flight (BR-32); a usage limit disables the whole composer but keeps the help links usable (BR-33).
 */

import type { FormEvent, KeyboardEvent } from 'react';
import { Info, Send } from 'lucide-react';
import { HELP_MESSAGE_MAX_CHARS, HELP_TEXT_REJECTED_INPUT } from '@resida/api-types/help';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { ContactSupportLabel } from './ChatMessage';
import type { ChatRateLimit } from './useHelpChat';

interface ChatComposerProps {
  draft: string;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  /** A turn is in flight. */
  busy: boolean;
  rateLimit: ChatRateLimit | null;
  /** A server-side rejection of the last message, shown under the field. */
  inputError: string | null;
  onSearchHelp: () => void;
  onContactSupport: () => void;
  /** The conversation is being attached for support: Contact support shows a busy state. */
  contactBusy: boolean;
}

export function ChatComposer({
  draft,
  onDraftChange,
  onSend,
  busy,
  rateLimit,
  inputError,
  onSearchHelp,
  onContactSupport,
  contactBusy,
}: ChatComposerProps) {
  const overLimit = draft.length > HELP_MESSAGE_MAX_CHARS;
  const fieldError = overLimit ? HELP_TEXT_REJECTED_INPUT : inputError;
  const canSend = !rateLimit && !busy && draft.trim() !== '' && !overLimit;
  // A usage limit disables the field, so the "n / 2000" counter has nothing to count (design board 8).
  const showCounter = !rateLimit;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (canSend) onSend();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (canSend) onSend();
  };

  return (
    <form onSubmit={submit} className="flex flex-none flex-col gap-2 border-t border-border bg-card px-4 py-3" data-testid="help-chat-composer">
      {rateLimit && (
        <div
          role="status"
          data-testid="help-rate-limited"
          className="flex gap-2.5 rounded-lg border border-warning/25 bg-warning/10 p-3 text-[13px]"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
          <span className="text-foreground">{rateLimit.text}</span>
        </div>
      )}

      <div
        className={cn(
          'flex flex-col rounded-lg border bg-background focus-within:ring-2 focus-within:ring-ring',
          fieldError ? 'border-destructive' : 'border-input',
          rateLimit && 'opacity-60',
        )}
      >
        <textarea
          value={draft}
          onChange={(event) => onDraftChange(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={Boolean(rateLimit)}
          rows={2}
          placeholder="Ask about using Resida"
          aria-label="Your message"
          aria-describedby={showCounter ? 'help-chat-counter' : undefined}
          aria-invalid={fieldError !== null}
          data-testid="help-chat-input"
          className={cn(
            'max-h-40 min-h-[64px] w-full resize-none bg-transparent px-3 pt-3 text-base text-foreground placeholder:text-muted-foreground focus-visible:outline-none md:text-sm',
            showCounter ? 'pb-1' : 'pb-3',
          )}
        />
        {showCounter && (
          <span
            id="help-chat-counter"
            data-testid="help-chat-counter"
            className={cn('self-end px-3 pb-2 text-xs', overLimit ? 'text-destructive' : 'text-muted-foreground')}
          >
            {draft.length} / {HELP_MESSAGE_MAX_CHARS}
          </span>
        )}
      </div>

      {fieldError && (
        <p role="alert" className="text-xs text-destructive" data-testid="help-chat-input-error">
          {fieldError}
        </p>
      )}

      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center">
          {rateLimit && (
            <Button
              type="button"
              variant="ghost"
              className="px-2 text-primary underline underline-offset-2"
              onClick={onSearchHelp}
              data-testid="help-search-articles"
            >
              Search help articles
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            className="px-2 text-primary underline underline-offset-2"
            onClick={onContactSupport}
            disabled={contactBusy}
            aria-busy={contactBusy}
            data-testid="help-contact-support"
          >
            <ContactSupportLabel busy={contactBusy} />
          </Button>
        </div>
        <Button type="submit" disabled={!canSend} aria-label="Send message" data-testid="help-chat-send">
          <Send aria-hidden="true" />
          Send
        </Button>
      </div>
    </form>
  );
}
